import {
  useCreateRoleWithUsersMutation,
  useGetAllModulesQuery,
  useGetRoleUsersQuery,
  useUpdateRoleWithUsersMutation,
} from '@/app/features/control-panel/role-permission/role-permission.api';
import { useGetAllEmployeesQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import EmployeeSelect from '@/components/EmployeeSelect';
import useAuth from '@/hooks/useAuth';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Checkbox, Flex, Form, Input } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useEffect, useMemo } from 'react';

type Props = CustomModalProps & { id?: string; data: any };

// Hardcoded modules removed, now fetched from API

const ManageRole = ({ open, handleClose, id, data }: Props) => {
  const [form] = Form.useForm();
  const { organizationId } = useAuth();
  const assignedEmployees = Form.useWatch('assigned_employees', form);

  const [createRoleWithUser, { isLoading: isCreating }] =
    useCreateRoleWithUsersMutation();
  const [updateRole, { isLoading: isUpdating }] =
    useUpdateRoleWithUsersMutation();

  const { data: employeesData } = useGetAllEmployeesQuery({
    params: { skip: 0, limit: 1000, status: 'active' },
  });

  // Get all available modules
  const { data: modulesData } = useGetAllModulesQuery(
    { organizationId },
    { skip: !organizationId },
  );

  const modules = useMemo(() => modulesData?.data || [], [modulesData]);

  // Get users assigned to this role
  const {
    data: roleUsersData,
    isFetching: isRoleUsersFetching,
    isError: isRoleUsersError,
  } = useGetRoleUsersQuery(
    { roleId: id as string, organizationId },
    { skip: !organizationId || !id },
  );

  useEffect(() => {
    if (!id || !data || !employeesData?.data?.data) return;
    // Wait for the role-users query to finish so stale cached data from a
    // previously-opened role panel doesn't pre-populate this form.
    if (isRoleUsersFetching) return;

    // API returns 404 when the role has no assigned users — treat that as
    // an empty assignment rather than letting stale form values linger.
    if (isRoleUsersError) {
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        permissions: modules?.map((m: any) => {
          const existing = (
            Array.isArray(data?.permissions) ? data.permissions : []
          ).find((p: any) => p.module_id === m.id);
          return (
            existing || {
              access: 'RESTRICT',
              module_id: m.id,
              module_name: m.name,
            }
          );
        }),
        assigned_employees: {},
        selected_employees: {},
      });
      return;
    }

    const assignedUsers = roleUsersData?.data || [];
    const assignedEmployeesByDept: Record<string, string[]> = {};

    // Get full user details for assigned users
    const assignedUserDetails = employeesData.data.data.filter((emp: any) =>
      assignedUsers.some(
        (assignment: any) => assignment.user_id === emp.user_id,
      ),
    );

    // Group by department
    assignedUserDetails.forEach((user) => {
      const deptName = user.department?.name || 'No Department';
      if (!assignedEmployeesByDept[deptName]) {
        assignedEmployeesByDept[deptName] = [];
      }
      assignedEmployeesByDept[deptName].push(user.user_id);
    });

    const existingPermissions = Array.isArray(data?.permissions)
      ? data.permissions
      : [];
    const mergedPermissions = modules?.map((m: any) => {
      const existing = existingPermissions?.find(
        (p: any) => p.module_id === m.id,
      );
      return (
        existing || {
          access: 'RESTRICT',
          module_id: m.id,
          module_name: m.name,
        }
      );
    });

    form.setFieldsValue({
      name: data.name,
      description: data.description,
      permissions: mergedPermissions,
      assigned_employees: assignedEmployeesByDept,
      selected_employees: assignedEmployeesByDept,
    });
  }, [
    id,
    data,
    roleUsersData,
    isRoleUsersFetching,
    isRoleUsersError,
    employeesData,
    form,
    modules,
  ]);

  // Create mode: set default permissions when modules load
  useEffect(() => {
    if (id || modules.length === 0) return;

    form.setFieldsValue({
      permissions: modules.map((m: any) => ({
        access: 'RESTRICT',
        module_id: m.id,
        module_name: m.name,
      })),
    });
  }, [id, modules, form, open]);

  const handleSubmit = async (values: any) => {
    try {
      const { assigned_employees, selected_employees, ...rest } = values || {};

      const userIds = Object.values(
        assigned_employees || {},
      ).flat() as string[];

      if (id) {
        // Update existing role
        const rolePayload = {
          ...rest,
          role_id: id,
          // organization_id: organizationId,
          user_ids: userIds,
        };

        await updateRole(rolePayload).unwrap();
        toast('success', 'Role updated successfully');
      } else {
        // Create new role
        const rolePayload = {
          ...rest,
          user_ids: userIds,
          organization_id: organizationId,
        };

        await createRoleWithUser(rolePayload).unwrap();
        toast('success', 'Role created successfully');
      }

      handleClose();
      form.resetFields();
    } catch (error: any) {
      const message = getErrorMessage(error);
      toast('error', message || `Failed to ${id ? 'update' : 'create'} role`);
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={id ? 'Edit Role' : 'Create Role'}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form space-y-6"
        onFinish={handleSubmit}
      >
        <Form.Item initialValue={{}} noStyle name={'assigned_employees'} />
        {/* Role Name */}
        <Form.Item
          name={'name'}
          label="Role Name"
          rules={[{ required: true, message: 'Please enter role name' }]}
        >
          <Input />
        </Form.Item>

        {/* Description */}
        <Form.Item
          rules={[{ required: true, message: 'Please enter description' }]}
          label="Description"
          name={'description'}
        >
          <TextArea />
        </Form.Item>

        <div className="text-white font-medium text-base border-b border-b-slate-700 pb-3">
          Permission<span className="text-theme-error-500 ml-1">*</span>
        </div>
        <div>
          <Form.List name="permissions">
            {(fields) => (
              <>
                {fields.map((field) => {
                  const module = modules?.[field.name] || {};
                  return (
                    <>
                      <Form.Item name={[field.name, 'module_id']} hidden />
                      <Form.Item name={[field.name, 'module_name']} hidden />
                      <Form.Item
                        {...field}
                        name={[field.name, 'access']}
                        label={module.name}
                        layout="horizontal"
                        className="horizontal-end"
                        getValueProps={(value) => ({
                          value: [value].filter(Boolean),
                        })}
                      >
                        <Checkbox.Group
                          onChange={(checkedValues) => {
                            const lastValue =
                              checkedValues?.[checkedValues?.length - 1];
                            form.setFieldValue(
                              ['permissions', field.name, 'access'],
                              lastValue || 'RESTRICT',
                            );
                          }}
                        >
                          <Checkbox
                            className="text-white custom-check"
                            value={'VIEW'}
                          >
                            View
                          </Checkbox>
                          <Checkbox
                            className="text-white custom-check"
                            value={'EDIT'}
                          >
                            Edit
                          </Checkbox>
                        </Checkbox.Group>
                      </Form.Item>
                    </>
                  );
                })}
              </>
            )}
          </Form.List>
          {/* <Form.Item
            name={'report_generate'}
            label={'Report Generate'}
            layout="horizontal"
            className="horizontal-end"
            initialValue={'YES'}
            getValueProps={(value) => ({
              value: [value].filter(Boolean),
            })}
          >
            <Checkbox.Group
              onChange={(checkedValues) => {
                const lastValue = checkedValues?.[checkedValues?.length - 1];
                form.setFieldValue(
                  'report_generate',
                  lastValue || 'YES', // Defaulting back to YES if unchecked as per initialValue
                );
              }}
            >
              <Checkbox className="text-white custom-check" value={'YES'}>
                Yes
              </Checkbox>
              <Checkbox className="text-white custom-check" value={'NO'}>
                No
              </Checkbox>
            </Checkbox.Group>
          </Form.Item> */}
        </div>

        {/* New Integrated Employee Assignment */}
        <Form.Item
          className="label-full"
          label={
            <Flex justify="space-between" className="!w-full grow">
              <div>User Assign</div>
              {/* <Button className="h-6 rounded-md !bg-transparent text-xs border-theme-primary text-theme-primary">
                Bulk Assign
                <UploadIcon size={16} />
              </Button> */}
            </Flex>
          }
        >
          <EmployeeSelect
            label=""
            form={form}
            employees={(employeesData?.data?.data as any) || []}
          />
        </Form.Item>

        {/* Selected Employees List */}
        <EmployeeSelect.Selected
          form={form}
          allowRemoveEmployee
          employees={(employeesData?.data?.data as any) || []}
          assignedEmployees={assignedEmployees}
        />

        <Flex justify="end">
          <Button
            loading={isCreating || isUpdating}
            htmlType="submit"
            className="btn-primary min-w-20"
          >
            {id ? 'Update' : 'Save'}
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default ManageRole;
